import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Currency, TimeRange, DashboardFilters, ViewGranularity } from '../types/dashboard.types';

// Get current month in YYYY-MM format
const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Get current date in YYYY-MM-DD format
const getCurrentDate = () => {
  return new Date().toISOString().split('T')[0];
};

// Check if a date is within the daily data range (3 months)
const isWithinDailyRange = (dateStr: string): boolean => {
  const date = new Date(dateStr);
  const now = new Date();
  const cutoff = new Date(now.getFullYear(), now.getMonth() - 3, 1);
  return date >= cutoff;
};

interface DashboardStore {
  filters: DashboardFilters;
  setTimeRange: (timeRange: TimeRange) => void;
  setBaseCurrency: (currency: Currency) => void;
  setSelectedMonth: (month: string) => void;
  setSelectedDate: (date: string) => void;
  setViewGranularity: (granularity: ViewGranularity) => void;
  setCustomDateRange: (start: string, end: string) => void;
  resetFilters: () => void;
  canViewDaily: (month?: string) => boolean;
}

const defaultFilters: DashboardFilters = {
  timeRange: 'month',
  baseCurrency: 'EUR',
  selectedMonth: getCurrentMonth(),
  selectedDate: getCurrentDate(),
  viewGranularity: 'monthly', // Default to monthly view for accurate totals
};

export const useDashboardStore = create<DashboardStore>()(  
  persist(
    (set, get) => ({
      filters: defaultFilters,
      
      setTimeRange: (timeRange) =>
        set((state) => ({
          filters: {
            ...state.filters,
            timeRange,
            customDateRange: timeRange !== 'custom' ? undefined : state.filters.customDateRange,
            selectedMonth: timeRange === 'month' ? (state.filters.selectedMonth || getCurrentMonth()) : state.filters.selectedMonth,
          },
        })),
      
      setBaseCurrency: (baseCurrency) =>
        set((state) => ({
          filters: {
            ...state.filters,
            baseCurrency,
          },
        })),
      
      setSelectedMonth: (selectedMonth) =>
        set((state) => {
          // Check if this month allows daily view
          const canViewDaily = isWithinDailyRange(`${selectedMonth}-01`);
          return {
            filters: {
              ...state.filters,
              timeRange: 'month',
              selectedMonth,
              // Auto-switch to monthly view if data is older than 3 months
              viewGranularity: canViewDaily ? state.filters.viewGranularity : 'monthly',
            },
          };
        }),
      
      setSelectedDate: (selectedDate) =>
        set((state) => ({
          filters: {
            ...state.filters,
            selectedDate,
            viewGranularity: 'daily',
            // Update month to match the selected date
            selectedMonth: selectedDate.slice(0, 7),
          },
        })),
      
      setViewGranularity: (viewGranularity) =>
        set((state) => ({
          filters: {
            ...state.filters,
            viewGranularity,
          },
        })),
      
      setCustomDateRange: (start, end) =>
        set((state) => ({
          filters: {
            ...state.filters,
            timeRange: 'custom',
            customDateRange: { start, end },
          },
        })),
      
      resetFilters: () => set({ filters: defaultFilters }),
      
      // Check if a month can be viewed with daily granularity
      canViewDaily: (month?: string) => {
        const targetMonth = month || get().filters.selectedMonth;
        if (!targetMonth) return true;
        return isWithinDailyRange(`${targetMonth}-01`);
      },
    }),
    {
      name: 'adwstats-dashboard-storage',
      version: 2,
      migrate: (persistedState: any, version: number) => {
        if (version < 2) {
          // v2: Change default granularity from 'daily' to 'monthly'
          if (persistedState?.filters?.viewGranularity === 'daily') {
            persistedState.filters.viewGranularity = 'monthly';
          }
        }
        return persistedState;
      },
    }
  )
);
